import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useLocation, useParams, Outlet, useNavigate } from 'react-router-dom'
import type { Playlist } from 'types/playlist'
import { loadPlaylistVersions } from '@disco/state-management/actions/playlist-actions'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import ModalWindow from 'components/ui/modal-window'
import Spinner from 'components/ui/spinner'
type Props = {
  withTracks?: boolean
  children?: React.ReactNode
}

/**
 * PlaylistLoadingContainer component for managing playlist loading state
 *
 * A container component that ensures playlist data is loaded before rendering its children.
 * Shows a loading spinner in a modal while the playlist is being fetched. Optionally
 * waits for tracks to be loaded as well.
 *
 * @param props - Component props
 * @param props.withTracks - Whether to wait for tracks to be loaded as well
 * @returns JSX element containing children if loaded, or loading modal
 */
const PlaylistLoadingContainer = (
  props: Props,
): React.ReactNode | React.ReactNode => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ playlistId: string }>()
  const playlistId = parseInt(params.playlistId!, 10)
  const playlist: Playlist | null | undefined = useSelector(state =>
    getPlaylist(state, {
      id: playlistId,
    }),
  )
  const playlistLoaded = Boolean(
    props.withTracks ? playlist && playlist.tracks.length !== 0 : playlist,
  )
  useEffect(() => {
    if (!playlistLoaded) {
      dispatch(loadPlaylistVersions(playlistId, props.withTracks))
    }
  }, [])

  if (playlistLoaded) {
    return props.children || <Outlet />
  }

  return (
    <ModalWindow
      header={null}
      width="medium"
      isOpen
      onClose={() => navigate('/')}
    >
      <Spinner />
    </ModalWindow>
  )
}

export default PlaylistLoadingContainer
